import { Grid, Box, Hidden, Typography } from "@material-ui/core";
import { Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import FormAutoComplete, {
  FormSelect,
} from "../../../forms/formik/FormSelectField";
import FormTextInput from "../../../forms/formik/FormTextInput";
import {
  CartItems,
  InvestorFundDetails,
  Options,
  YES,
} from "../../../../store/types";
import InvestorDetailsPreview from "../InvestorDetails";
import * as yup from "yup";
import {
  CalcSIPEnddt,
  addItemsToCart,
  getMinimumAmount,
  getSwpDayOptions,
  getWithdrawalFrequency,
  getWithdrawalOptions,
  updateCartItem,
} from "../../../../store/actions";
import CustomButton from "../../../forms/CustomButton";
import SubmitButton from "../../../forms/formik/FormSubmitButton";
import {
  SwitchDetails,
  getFrequencyName,
} from "../../../home/transactions/tranUtils";
import { useAppSelector } from "../../../../store/hooks";
import { convertDate, getSelectedCartItem } from "../../../../utils/cart";
import { getSwpFolios } from ".";

const validationSchema = (fundDetails: InvestorFundDetails[]) =>
  yup.object().shape({
    amc: yup.string().required("AMC is required."),
    folio: yup
      .string()
      .required("Folio is required.")
      .test("zbf", "Zero balance folios are not allowed", function (value) {
        const { amc } = this.parent;
        const schemesList = fundDetails.reduce((acc, curr) => {
          const isValidScheme = [
            curr.Fund === amc,
            String(curr.Acno) === String(value),
            curr.BalUnits,
          ].every(Boolean);

          if (!isValidScheme) return acc;

          const scheme = {
            label: `${curr.SchDesc1} - ${curr.PlnDesc} - ${curr.OptDesc}`,
            value: `${curr.SchDesc1} - ${curr.PlnDesc} - ${curr.OptDesc}`,
            ...curr,
          };

          return [...acc, scheme];
        }, []);
        return Boolean(schemesList.length);
      }),
    scheme: yup.string().required("Scheme is required."),
    withdrawalOption: yup.string().required("Withdrawal Option is required."),
    noOfWithdrawals: yup
      .string()
      .required("Number of Withdrawals is required.")
      .test("minWithdrawals", "", function (value) {
        const { minimumWithdrawals = 2 } = this.parent;
        if (parseInt(value) < minimumWithdrawals)
          return this.createError({
            message: `Minimum ${minimumWithdrawals} withdrawals required`,
          });

        if (parseInt(value) > 500)
          return this.createError({
            message: "Cannot exceed  maximum 500 withdrawals ",
          });

        return true;
      }),
    withdrawalFrequency: yup
      .string()
      .required("Withdrawal Frequency is required."),
    swpDay: yup.string().required("SWP Day is required."),
    withdrawalStartDate: yup
      .string()
      .required("Withdrawal Start Date is required."),
    withdrawalEndDate: yup
      .string()
      .required("Withdrawal End Date is required."),
    amount: yup.string().when("withdrawalOption", {
      is: "F",
      then: (schema) => {
        return schema
          .required("Amount is required")
          .test("minimumAmount", "", function (value) {
            const { minAmount, maxAmount } = this.parent;
            if (!minAmount) return true;

            if (parseFloat(maxAmount) * (1 + 0.05) < parseFloat(minAmount)) {
              return this.createError({
                message: `You do not have enough balance to start an SWP transaction`,
              });
            }

            if (
              parseFloat(value?.replaceAll(",", "")) * (1 + 0.05) <
              parseFloat(minAmount)
            ) {
              return this.createError({
                message: `Minimum amount (${minAmount}) is required`,
              });
            }
            return true;
          })
          .test(
            "maximumAmount",
            "Cannot exceed the current value",
            function (value) {
              const { maxAmount } = this.parent;
              if (!value)
                return this.createError({
                  message: "Amount is required",
                });

              if (!maxAmount) return true;
              if (parseInt(maxAmount.replaceAll(",", "")) >= parseInt(value))
                return true;

              return this.createError({
                message: `Cannot exceed ${maxAmount}`,
              });
            }
          );
      },
      otherwise: (schema) => schema.notRequired(),
    }),
  });

type Values = {
  amc: string;
  folio: string | number;
  scheme: string;
  withdrawalOption: string;
  noOfWithdrawals: string | number;
  withdrawalFrequency: string;
  swpDay: string | number;
  withdrawalStartDate: string;
  withdrawalEndDate: string;
  amount?: string;
  minAmount?: string;
  maxAmount?: string;
  minimumWithdrawals?: string;
};

export const SWP = () => {
  const dispatch = useDispatch();

  const location = useLocation<{
    cartId: string;
    cartItemId: string | number;
    amc: string;
  }>();

  const history = useHistory();

  const { state, pathname } = location;

  const isUpdateItem = pathname.includes("/edit-transaction");

  const cartReducer = useAppSelector((state) => state.cartReducer);

  const { fundDetails } = cartReducer;

  // @ts-ignore
  const cartItems: CartItems<"SWP"> = cartReducer.cartItems;

  const { payload } = getSelectedCartItem(cartItems, state);

  const [initialState, setInitialState] = useState<Values>({
    amc: "",
    folio: "",
    noOfWithdrawals: "",
    scheme: "",
    swpDay: "",
    withdrawalEndDate: "",
    withdrawalFrequency: "",
    withdrawalOption: "",
    withdrawalStartDate: "",
    amount: "",
  });
  const [fundOptions, setFundOptions] = useState<Options>([]);
  const [folioOptions, setFolioOptions] = useState<Options>([]);
  const [schemeOptions, setSchemeOptions] = useState<
    Array<InvestorFundDetails & { value: string; label: string }>
  >([]);

  const [withdrawalOptions, setWithdrawalOptions] = useState<Options>([]);
  const [withdrawalFrequencyOptions, setWithdrawalFrequencyOptions] = useState(
    []
  );
  const [swpDayOptions, setSwpDayOptions] = useState<Options>([]);

  const onSubmit = async (values: Values) => {
    const { cartItemId, cartId } = state;

    const selectedCartItem = cartItems.find(
      ({ cartItemId: id }) => id === cartItemId
    );
    const { payload } = selectedCartItem;

    const mobile =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.Mobile || "";
    const email =
      // @ts-ignore
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.email || "";

    const {
      amc,
      folio,
      scheme,
      amount,
      noOfWithdrawals,
      swpDay,
      withdrawalEndDate,
      withdrawalFrequency,
      withdrawalOption,
      withdrawalStartDate,
    } = values;

    const selectedScheme = schemeOptions.find(({ value }) => value === scheme);

    const requestBody = {
      ...payload,
      email,
      mobile,
      fund: amc,
      folio: String(folio),
      scheme: selectedScheme.Sch,
      plan: selectedScheme.Pln,
      option: selectedScheme.Opt,
      amount: withdrawalOption === "F" ? amount : 0,
      withdrawalType:
        withdrawalOption === "F" ? "Fixed" : "Capital Appreciation",
      noOfWithdrawals,
      frequency: getFrequencyName(withdrawalFrequency),
      swpFrequency: withdrawalFrequency,
      swpOption: withdrawalOption === "F" ? "Fixed" : "Capital Appreciation",
      swpStartDate: convertDate(withdrawalStartDate),
      swpEndDate: convertDate(withdrawalEndDate),
      schemeName: selectedScheme.label,
      swpDay,
      cartItemId,
      cartId,
      taxSaverFlag: selectedScheme.TaxSaverFlag,
    };

    const response = await dispatch(updateCartItem(requestBody));

    if (!!response) {
      alert("Item has been updated successfully");
      history.push("/cart/investor-cart", {
        pan: payload.pan,
      });
    }
  };

  const onAddNewTransaction = async (values: Values) => {
    const mobile =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.Mobile || "";
    const email =
      fundDetails.find(({ Acno, Fund, PAN }) => {
        if (values.amc !== Fund) return false;
        if (values.folio.toString() !== Acno.toString()) return false;
        if (payload.pan !== PAN) return false;
        return true;
      })?.email || "";

    const {
      amc,
      folio,
      scheme,
      amount,
      noOfWithdrawals,
      swpDay,
      withdrawalEndDate,
      withdrawalFrequency,
      withdrawalOption,
      withdrawalStartDate,
    } = values;
    const selectedScheme = schemeOptions.find(({ value }) => value === scheme);
    const selectedFund = fundOptions.find((i) => i.value === amc).label;

    const requestBody = {
      ...payload,
      email,
      mobile,
      fund: amc,
      folio: String(folio),
      scheme: selectedScheme.Sch,
      plan: selectedScheme.Pln,
      option: selectedScheme.Opt,
      amount: withdrawalOption === "F" ? amount : 0,
      withdrawalType:
        withdrawalOption === "F" ? "Fixed" : "Capital Appreciation",
      noOfWithdrawals,
      frequency: getFrequencyName(withdrawalFrequency),
      swpFrequency: withdrawalFrequency,
      swpOption: withdrawalOption === "F" ? "Fixed" : "Capital Appreciation",
      swpStartDate: convertDate(withdrawalStartDate),
      swpEndDate: convertDate(withdrawalEndDate),
      schemeName: selectedScheme.label,
      swpDay,
      taxSaverFlag: selectedScheme.TaxSaverFlag,
      fundName: selectedFund,
    };

    const response = await dispatch(addItemsToCart(requestBody));

    if (!!response) {
      history.push("/cart/investor-cart", {
        pan: payload.pan,
      });
    }
  };

  const cancelTransaction = () =>
    history.replace("/cart/investor-cart", {
      pan: payload.pan,
    });
  const handleAmcChange = useCallback(
    (setFieldValue: (field: string, value: any) => void, amc: any) => {
      const newFolioOptions = getSwpFolios(fundDetails, amc);
      setFolioOptions(Array.from(new Set(newFolioOptions)));
      setFieldValue("folio", "");
      setFieldValue("amc", amc);
    },
    [fundDetails]
  );

  const handleFolioChange = async (
    setValues: (arg1: Values) => void,
    values: Values,
    setFieldError: (fieldName: string, error: string) => void,
    folio: any
  ) => {
    setValues({
      ...values,
      folio,
      scheme: "",
      amount: "",
    });

    // @ts-ignore
    const schemesList = fundDetails.reduce((acc, curr) => {
      const isValidScheme = [
        curr.Fund === values.amc,
        String(curr.Acno) === String(folio),
        curr.BalUnits,
      ].every(Boolean);

      if (!isValidScheme) return acc;

      const scheme = {
        label: `${curr.SchDesc1} - ${curr.PlnDesc} - ${curr.OptDesc}`,
        value: `${curr.SchDesc1} - ${curr.PlnDesc} - ${curr.OptDesc}`,
        ...curr,
      };

      return [...acc, scheme];
    }, []);
    setSchemeOptions(schemesList);
  };

  const handleSwpDayChange = async (
    values: Values,
    setFieldValue: (arg1: string, arg2: string) => void,
    swpDay: any
  ) => {
    const selectedScheme = schemeOptions.find(
      ({ value }) => value === values.scheme
    ).Sch;

    const params = {
      Fund: values.amc,
      StartDate: swpDay,
      installments: values.noOfWithdrawals,
      Frequency: values.withdrawalFrequency,
      trtype: "SWP",
      sch: selectedScheme,
    };

    const response = await CalcSIPEnddt(params)(dispatch);

    const startDate = response.DtData[0].SIP_StartDate;
    const endDate = response.DtData[0].SIP_EndDate;

    setFieldValue("withdrawalStartDate", startDate);
    setFieldValue("withdrawalEndDate", endDate);
  };

  const setInitialValues = async () => {
    const { folio } = payload;
    const newFolioOptions = getSwpFolios(fundDetails, state.amc);
    setFolioOptions(newFolioOptions);
    const schemesList = fundDetails
      .filter((fund) =>
        [
          fund.Fund === state.amc,
          fund.Acno.toString() === folio.toString(),
        ].every(Boolean)
      )
      .map((fund) => ({
        ...fund,
        label: `${fund.SchDesc1} - ${fund.PlnDesc} - ${fund.OptDesc}`,
        value: `${fund.SchDesc1} - ${fund.PlnDesc} - ${fund.OptDesc}`,
      }));
    setSchemeOptions(schemesList);
    const wdOptions = await getWithdrawalFrequency(state.amc)(dispatch);

    setWithdrawalOptions(await getWithdrawalOptions(state.amc)(dispatch));
    setWithdrawalFrequencyOptions(wdOptions);
    setSwpDayOptions(
      await getSwpDayOptions(state.amc, payload.swpFrequency)(dispatch)
    );
    const selectedSchemeOption = schemesList.find(({ Sch, Pln, Opt }) =>
      [
        Sch === payload.scheme,
        Pln === payload.plan,
        Opt === payload.option,
      ].every(Boolean)
    );

    const minAmountPayload = {
      Fund: state.amc,
      Scheme: selectedSchemeOption.Sch,
      Trtype: "SWP",
      Frequency: getFrequencyName(payload.frequency),
    };
    const minAmountResponse = await getMinimumAmount(minAmountPayload)(
      dispatch
    );
    const { minimumInstallments } = wdOptions.find(
      ({ value }) => value === payload.swpFrequency
    );

    const { minamt } = minAmountResponse.DtData[0];
    setInitialState((prev) => ({
      ...prev,
      amount: payload.amount.toString(),
      amc: state.amc,
      folio,
      noOfWithdrawals: payload.noOfWithdrawals,
      scheme: selectedSchemeOption.value,
      swpDay: payload.swpDay,
      withdrawalEndDate: convertDate(payload.swpEndDate),
      withdrawalFrequency: payload.swpFrequency,
      withdrawalOption: payload.swpOption === "Fixed" ? "F" : "C",
      withdrawalStartDate: convertDate(payload.swpStartDate),
      minAmount: payload.swpOption === "Fixed" ? minamt : "",
      minimumWithdrawals: minimumInstallments,
    }));
  };

  useEffect(() => {
    const newFundOptions = fundDetails.reduce((acc, { Fund, FundDesc }) => {
      if (!acc.find(({ value }) => value === Fund)) {
        return [...acc, { label: FundDesc, value: Fund }];
      }
      return acc;
    }, []);

    setFundOptions(newFundOptions);

    if (pathname.includes("add-transaction")) return;
    try {
      setInitialValues();
    } catch (error) {
      history.replace("/new-transaction");
    }
  }, [dispatch, pathname, fundDetails, history]);

  return (
    <Grid container>
      <Grid item lg={9} sm={12}>
        <Box
          sx={{
            margin: "40px",
            marginRight: "10px",
            bgcolor: "white",
            padding: "18px 24px 18px 24px",
            boxShadow: "0px 0px 8px 0px rgba(202, 202, 202, 1);",
            borderRadius: "4px",
          }}
        >
          <Typography
            component={"h4"}
            style={{
              fontWeight: "800",
              fontSize: "24px",
              marginBottom: "20px",
              fontFamily: "OpenSans-SemiBold",
            }}
          >
            SWP
          </Typography>
          <Formik
            initialValues={initialState}
            onSubmit={isUpdateItem ? onSubmit : onAddNewTransaction}
            validationSchema={validationSchema(fundDetails)}
            enableReinitialize
            validateOnChange
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              setValues,
              setFieldError,
            }) => {
              const handleFrequencyChange = async (e: any) => {
                const selectedSchemeOption = schemeOptions.find(
                  ({ value, Acno }) =>
                    values.scheme === value && values.folio == Acno
                );
                const payload = {
                  Fund: values.amc,
                  Scheme: selectedSchemeOption.Sch,
                  Trtype: "SWP",
                  Frequency: e.target.value,
                };
                const minAmountResponse = await getMinimumAmount(payload)(
                  dispatch
                );

                const { DtData } = minAmountResponse;

                const { minamt, mininst } = DtData[0];

                const response = await getSwpDayOptions(
                  values.amc,
                  e.target.value
                )(dispatch);
                setSwpDayOptions(response);
                setValues({
                  ...values,
                  withdrawalFrequency: e.target.value,
                  noOfWithdrawals: "",
                  swpDay: "",
                  withdrawalStartDate: "",
                  withdrawalEndDate: "",
                  minAmount: minamt,
                  minimumWithdrawals: mininst,
                });
              };
              const handleSchemeChange = async (
                fundCode: string,
                scheme: any
              ) => {
                const selectedScheme = schemeOptions.find(({ value, Acno }) => {
                  return value === scheme && values.folio === Acno.toString();
                });

                setWithdrawalOptions(
                  await getWithdrawalOptions(fundCode)(dispatch)
                );

                setWithdrawalFrequencyOptions(
                  await getWithdrawalFrequency(fundCode)(dispatch)
                );
                setValues({
                  ...values,
                  maxAmount: selectedScheme?.CurValue,
                  withdrawalStartDate: "",
                  withdrawalEndDate: "",
                  noOfWithdrawals: "",
                  amount: "",
                  swpDay: "",
                  withdrawalFrequency: "",
                  withdrawalOption: "",
                  scheme: scheme,
                });
              };

              const selectedScheme = schemeOptions.find(
                ({ value }) => values.scheme === value
              );

              const isMultiBrokeredScheme =
                selectedScheme?.multibrokflg === "Y";

              const handleWithdrawalsChange = (e: any) => {
                setValues({
                  ...values,
                  noOfWithdrawals: e.target.value,
                  withdrawalStartDate: "",
                  withdrawalEndDate: "",
                  swpDay: "",
                  amount: "",
                  withdrawalOption: "",
                });
              };

              return (
                <Box component={"form"} onSubmit={handleSubmit}>
                  <FormAutoComplete
                    disabled={isUpdateItem}
                    name="amc"
                    label="AMC"
                    placeholder="Select AMC"
                    options={fundOptions}
                    onChange={(e) =>
                      handleAmcChange(setFieldValue, e.target.value)
                    }
                  />
                  <FormSelect
                    name="folio"
                    label="Folio"
                    disabled={!values.amc}
                    options={folioOptions}
                    onChange={(e) =>
                      handleFolioChange(
                        setValues,
                        values,
                        setFieldError,
                        e.target.value
                      )
                    }
                  />
                  <FormAutoComplete
                    name="scheme"
                    label="Scheme"
                    disabled={!values.folio}
                    options={schemeOptions}
                    onChange={(e) => {
                      setFieldValue("scheme", e.target.value);
                      handleSchemeChange(values.amc, e.target.value);
                    }}
                  />
                  <Box
                    style={{
                      backgroundColor: "rgba(0, 124, 197, 0.1)",
                      display: "flex",
                      justifyContent: "space-evenly",
                      paddingTop: 20,
                      paddingBottom: 20,
                      marginBottom: 20,
                    }}
                  >
                    {!!values.folio ? (
                      <>
                        <div className="keys">
                          {<SwitchDetails name={"Balance Units"} />}
                          {<SwitchDetails name={"Current Value"} />}
                          <SwitchDetails name={"NAV as on Date"} />
                        </div>
                        <div className="values">
                          <SwitchDetails
                            name={selectedScheme?.BalUnits || ""}
                          />
                          <SwitchDetails
                            name={selectedScheme?.CurValue || ""}
                          />
                          <SwitchDetails name={selectedScheme?.LastNAV || ""} />
                        </div>
                      </>
                    ) : isMultiBrokeredScheme ? (
                      <>
                        <div
                          style={{
                            fontFamily: "OpenSans-SemiBold",
                            fontSize: 13,
                            color: "red",
                            margin: 10,
                          }}
                        >
                          {" "}
                          Note: This folio has multiple distributors. Hence, the
                          balance units and current value will not be displayed.
                          In case of full redemption, all the units in the
                          investor's portfolio will be redeemed.
                        </div>
                      </>
                    ) : null}
                  </Box>

                  <FormAutoComplete
                    name="withdrawalOption"
                    label="Withdrawal Option"
                    options={withdrawalOptions}
                  />
                  <FormAutoComplete
                    name="withdrawalFrequency"
                    label="Withdrawal Frequency"
                    options={withdrawalFrequencyOptions}
                    onChange={handleFrequencyChange}
                  />
                  <FormTextInput
                    name="noOfWithdrawals"
                    label={
                      values.minimumWithdrawals
                        ? `No of withdrawals (Minimum: ${values.minimumWithdrawals})`
                        : "No of withdrawals"
                    }
                    onChange={handleWithdrawalsChange}
                  />

                  <FormSelect
                    name="swpDay"
                    label="SWP Day"
                    options={swpDayOptions}
                    onChange={async (e) => {
                      setFieldValue("swpDay", e.target.value);
                      await handleSwpDayChange(
                        values,
                        setFieldValue,
                        e.target.value
                      );
                    }}
                    disabled={!Boolean(values.noOfWithdrawals)}
                  />
                  <Typography>Withdrawal Period</Typography>
                  <FormTextInput
                    name="withdrawalStartDate"
                    label="From Date"
                    disabled
                  />
                  <FormTextInput
                    name="withdrawalEndDate"
                    label="To Date"
                    disabled
                  />
                  {values.withdrawalOption === "F" ? (
                    <FormTextInput
                      name="amount"
                      label={
                        !!values.minAmount
                          ? `Investment Amount (Minimum ${values.minAmount})`
                          : "Investment Amount"
                      }
                      type="number"
                    />
                  ) : null}

                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <CustomButton
                      label="Go to Cart"
                      variant="outlined"
                      onClick={cancelTransaction}
                    />
                    <SubmitButton
                      label={isUpdateItem ? "Update" : "Add To Cart"}
                      variant="contained"
                    />
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </Box>
      </Grid>
      <Hidden mdDown>
        <Grid item lg={3}>
          <InvestorDetailsPreview
            investorData={[
              { label: "Investor Name", value: payload.investorName },
              { label: "PAN", value: payload.pan },
              { label: "Email", value: payload.email },
              { label: "Mobile", value: payload.mobile },
            ]}
          />
        </Grid>
      </Hidden>
    </Grid>
  );
};
